/**
 * Reward List Page
 * 
 * Displays filterable, paginated list of reward catalog items.
 * Uses useRewardData hook for clean separation of concerns.
 */

import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, type Column, Pagination, type SortConfig } from '@/components/common/Table';
import { RewardCategoryBadge, RewardStatusBadge, RewardVariantBadge } from '@/components/common/Badge';
import { RewardFilters } from '@/components/rewards/RewardFilters';
import { useRewardData } from '@/app/hooks/useRewardData';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import type { RewardResponse, RewardQueryParams } from '@/app/types/rewards';
import { formatPointCost } from '@/app/types/rewards';
import { auditService } from '@/app/services';
import { logger } from '@/app/utils/logger';

import './RewardList.scss';

const RewardList: React.FC = () => {
  const navigate = useNavigate();
  const { setActions } = useHeaderActions();
  
  const {
    rewards,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    filters,
    isLoading,
    error,
    applyFilters,
    goToPage,
    changePageSize,
    resetFilters,
    pageSizeOptions,
  } = useRewardData();

  // Set header actions
  useEffect(() => {
    setActions(
      <>
        <button 
          className="btn-refresh"
          onClick={() => applyFilters(filters)}
          disabled={isLoading}
        >
          ↻ Refresh
        </button>
        <button 
          className="btn-create"
          onClick={() => navigate('/rewards/new')}
        >
          + New Reward
        </button>
      </>
    );
    return () => setActions(null);
  }, [setActions, applyFilters, filters, isLoading, navigate]);

  // Sort state (client-side for now)
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null });

  // Handle filter changes (local state until search)
  const [localFilters, setLocalFilters] = useState<RewardQueryParams>(filters);
  
  // Sync local filters when Redux filters change
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Sort rewards client-side
  const sortedRewards = useMemo(() => {
    if (!sortConfig.direction || !sortConfig.key) return rewards;
    
    const { key, direction } = sortConfig;
    
    return [...rewards].sort((a, b) => {
      const aVal = a[key as keyof RewardResponse];
      const bVal = b[key as keyof RewardResponse];
      
      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return direction === 'asc' ? 1 : -1;
      if (bVal == null) return direction === 'asc' ? -1 : 1;
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Convert to strings for comparison
      const aStr = String(aVal);
      const bStr = String(bVal);
      
      // Check if it's a date string
      if (key.includes('date') || key.includes('_at') || key === 'created_at' || key === 'updated_at') {
        const aDate = new Date(aStr).getTime();
        const bDate = new Date(bStr).getTime();
        if (!isNaN(aDate) && !isNaN(bDate)) {
          return direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
      }
      
      // String comparison
      const result = aStr.localeCompare(bStr);
      return direction === 'asc' ? result : -result;
    });
  }, [rewards, sortConfig]);

  const handleFiltersChange = useCallback((newFilters: RewardQueryParams) => {
    setLocalFilters(newFilters);
  }, []);

  const handleSearch = useCallback(() => {
    applyFilters(localFilters);
  }, [applyFilters, localFilters]);

  // Log search audit after results return with accurate count
  const prevLoadingRef = React.useRef(isLoading);
  React.useEffect(() => {
    // Detect transition from loading to not loading (data just loaded)
    if (prevLoadingRef.current && !isLoading) {
      const filtersToLog: Record<string, unknown> = {};
      if (filters.search) filtersToLog.search = filters.search;
      if (filters.category) filtersToLog.category = filters.category;
      if (filters.status) filtersToLog.status = filters.status;
      if (filters.reward_variant) filtersToLog.reward_variant = filters.reward_variant;
      
      auditService.logRecordSearch('reward', filtersToLog, totalCount, '/rewards')
        .catch((err) => logger.warn('Failed to log record search', { error: err }));
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, totalCount, filters]);

  const handleClear = useCallback(() => {
    resetFilters();
    setLocalFilters({ limit: 100, offset: 0 });
  }, [resetFilters]);

  const handleRowClick = useCallback((reward: RewardResponse) => {
    navigate(`/rewards/${reward.reward_id}`);
  }, [navigate]);

  // Copy ID to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // Table columns
  const columns: Column<RewardResponse>[] = [
    {
      key: 'reward_key',
      header: 'Reward Key',
      width: '180px',
      sortable: true,
      render: (reward) => (
        <span 
          className="reward-key" 
          title="Click to copy ID"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(reward.reward_id);
          }}
        >
          {reward.reward_key}
        </span>
      ),
    },
    {
      key: 'reward_name',
      header: 'Name',
      width: '220px',
      sortable: true,
      render: (reward) => (
        <div className="title-cell">
          <span className="title">{reward.reward_name}</span>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '120px',
      sortable: true,
      render: (reward) => <RewardCategoryBadge category={reward.category} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      sortable: true,
      render: (reward) => <RewardStatusBadge status={reward.status} />,
    },
    {
      key: 'point_cost',
      header: 'Point Cost',
      width: '120px',
      sortable: true,
      align: 'right',
      render: (reward) => (
        <span className="point-cost">{formatPointCost(reward.point_cost)}</span>
      ),
    },
    {
      key: 'reward_variant',
      header: 'Variant',
      width: '120px',
      sortable: true,
      render: (reward) => <RewardVariantBadge variant={reward.reward_variant} />,
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (reward) => (
        <button 
          className="btn-view"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(reward);
          }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="reward-list-page">
      <RewardFilters
        filters={localFilters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={handleSearch}>Retry</button>
        </div>
      )}

      <div className="results-summary">
        {!isLoading && (
          <span>{totalCount.toLocaleString()} reward{totalCount !== 1 ? 's' : ''} found</span>
        )}
      </div>

      <Table<RewardResponse>
        data={sortedRewards}
        columns={columns}
        keyExtractor={(reward) => reward.reward_id}
        loading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No rewards found matching your criteria"
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalCount}
        itemsPerPage={pageSize}
        onPageChange={goToPage}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={changePageSize}
      />
    </div>
  );
};

export default RewardList;

